// =============================================================================
// src/core/section-manager.ts — Scroll-spy + ciclo de vida das secoes (porte).
//
// Proveniencia: assets/unified-app.js `var SectionManager = {...}` (linhas 683-802).
//   Portado FIEL — os timings/thresholds sao CONSTRAINT de paridade do usuario:
//     · IntersectionObserver thresholds [0,0.1,0.2,0.3,0.5,0.7,1.0]
//     · rootMargin '-25% 0px -25% 0px', dispara em intersectionRatio >= 0.3
//     · clique no nav-dot: suppressObserverUntil = now + 1100ms; reabilita em +1120ms
//     · invalidateSize do mapa 650ms apos ativar a secao (espera transicao CSS)
//   NAO alterar nenhum desses numeros.
//
// Diferenca do legado: em vez de `Sections[camelCase(id)]`, recebe a lista de
//   AppModule em init(views, ctx) e indexa por `module.id`. Despacha os hooks
//   opcionais activate(ctx)/deactivate() — preenchidos pelos agentes de comportamento
//   das views. Sem global `Sections`/`MapController`/`TopbarNav` escondido: tudo
//   vem do AppContext/AppBus injetados.
// =============================================================================

import type { AppContext, AppModule, SectionId } from '../app/types';
import { qs, qsa } from '../app/dom';
import { bindOnce } from './ui';

/**
 * AppModule com hooks opcionais de ciclo de vida da secao. O contrato base
 * (AppModule) so exige render()/init(); secoes que reagem ao scroll-spy
 * (mapa/comparador/cinema/grafo) implementam activate(ctx)/deactivate().
 * Equivalente ao SectionController legado (activate(map)/deactivate()), porem
 * recebendo o AppContext (que carrega o MapController em ctx.map).
 */
export interface SectionLifecycleModule extends AppModule {
  activate?(ctx: AppContext): void;
  deactivate?(): void;
}

/** Ms suprimindo o observer apos clique no nav-dot (evita briga com o smooth-scroll). */
const NAV_CLICK_SUPPRESS_MS = 1100;
/** Ms para reabilitar o observer e reconciliar o nav-rail apos o clique. */
const NAV_CLICK_RELEASE_MS = 1120;
/** Ms ate invalidar o tamanho do mapa apos ativar a secao (espera a transicao CSS). */
const MAP_INVALIDATE_DELAY_MS = 650;
/** Razao minima de interseccao para considerar uma secao ativa (scroll-spy). */
const ACTIVE_INTERSECTION_RATIO = 0.3;

/** Rotulos do breadcrumb por secao (SectionManager.updateBreadcrumb legado). */
const SECTION_BREADCRUMB_NAMES: Record<SectionId, string> = {
  'mapa-eleitoral': 'Mapa Eleitoral',
  'perfil-regiao': 'Perfil da Região',
  comparador: 'Comparador',
  demografica: 'Demografia',
  camadas: 'Estúdio de Camadas',
  negocios: 'Negócios',
  grafo: 'Grafo de Influências',
  impostometro: 'Impostômetro',
  monitoramento: 'Monitoramento',
  cinema: 'Modo Cinema',
};

/**
 * Orquestrador de scroll-spy das secoes scrollaveis (.u-section). Mantem a secao
 * ativa, despacha activate/deactivate e sincroniza nav-rail/breadcrumb. Singleton
 * exportado ao final (SectionManager) — mesma forma de uso do runtime legado.
 */
class ScrollSpySectionManager {
  activeSection: SectionId | null = null;
  paused = false;

  private sectionEls: HTMLElement[] = [];
  private observer: IntersectionObserver | null = null;
  private suppressObserverUntil = 0;
  private moduleById = new Map<SectionId, SectionLifecycleModule>();
  private context: AppContext | null = null;

  /** Pausa o scroll-spy (usado enquanto uma rota fullscreen/workspace esta aberta). */
  setPaused(on: boolean): void {
    this.paused = on;
  }

  /**
   * Liga o scroll-spy: indexa as views por id, observa as .u-section, liga os
   * cliques do nav-rail e ativa a primeira secao (mapa-eleitoral), como o legado.
   */
  init(views: readonly SectionLifecycleModule[], ctx: AppContext): void {
    this.context = ctx;
    this.indexViews(views);
    this.sectionEls = qsa<HTMLElement>('.u-section');
    this.observer = new IntersectionObserver((entries) => this.onIntersect(entries), {
      root: null,
      rootMargin: '-25% 0px -25% 0px',
      threshold: [0, 0.1, 0.2, 0.3, 0.5, 0.7, 1.0],
    });
    this.sectionEls.forEach((section) => this.observer?.observe(section));
    this.bindNavRail();
    this.activeSection = null;
    this.activateSection('mapa-eleitoral');
  }

  /** Indexa os modulos por seu id (substitui o lookup Sections[camelCase]). */
  private indexViews(views: readonly SectionLifecycleModule[]): void {
    views.forEach((view) => this.moduleById.set(view.id as SectionId, view));
  }

  /** Callback do observer: ativa a secao mais visivel (ratio >= 0.3) quando nao suprimido. */
  private onIntersect(entries: readonly IntersectionObserverEntry[]): void {
    if (this.paused || Date.now() < this.suppressObserverUntil) return;
    entries.forEach((entry) => {
      if (!entry.isIntersecting || entry.intersectionRatio < ACTIVE_INTERSECTION_RATIO) return;
      const sectionId = (entry.target as HTMLElement).dataset.section as SectionId | undefined;
      if (sectionId && sectionId !== this.activeSection) this.activateSection(sectionId);
    });
  }

  /** Liga o clique de cada .u-nav-dot (suprime observer + smooth-scroll ate o alvo). */
  private bindNavRail(): void {
    qsa<HTMLElement>('.u-nav-dot').forEach((dot) => {
      bindOnce(dot, 'click', 'nav-dot', () => this.onNavDotClick(dot));
    });
  }

  /** Clique no nav-dot: ativa a secao, suprime o observer e rola suavemente ate ela. */
  private onNavDotClick(dot: HTMLElement): void {
    this.suppressObserverUntil = Date.now() + NAV_CLICK_SUPPRESS_MS;
    const sectionId = dot.dataset.section as SectionId | undefined;
    if (sectionId && sectionId !== this.activeSection) this.activateSection(sectionId);
    qs(`#section-${sectionId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(() => {
      this.suppressObserverUntil = 0;
      this.updateNavRail(this.activeSection);
    }, NAV_CLICK_RELEASE_MS);
  }

  /**
   * Ativa uma secao: desativa a anterior, atualiza nav-rail/breadcrumb, alterna o
   * Modo Cinema, ativa o novo controller e invalida o mapa apos a transicao CSS.
   */
  activateSection(sectionId: SectionId): void {
    const prevSection = this.activeSection;
    this.activeSection = sectionId;
    this.deactivatePrevious(prevSection);
    this.updateNavRail(sectionId);
    if (!this.paused) this.updateBreadcrumb(sectionId);
    this.toggleCinemaMode(sectionId);
    this.activateCurrent(sectionId);
    setTimeout(() => this.context?.map?.invalidateSize(), MAP_INVALIDATE_DELAY_MS);
  }

  /** Chama deactivate() do controller da secao anterior, se existir. */
  private deactivatePrevious(prevSection: SectionId | null): void {
    if (!prevSection) return;
    this.moduleById.get(prevSection)?.deactivate?.();
  }

  /** Chama activate(ctx) do controller da secao atual, se existir. */
  private activateCurrent(sectionId: SectionId): void {
    if (!this.context) return;
    this.moduleById.get(sectionId)?.activate?.(this.context);
  }

  /** Modo Cinema alterna a classe body.cinema-active (mapa full-bleed). */
  private toggleCinemaMode(sectionId: SectionId): void {
    document.body.classList.toggle('cinema-active', sectionId === 'cinema');
  }

  /** Marca o nav-dot ativo (data-section === sectionId). */
  private updateNavRail(sectionId: SectionId | null): void {
    qsa<HTMLElement>('.u-nav-dot').forEach((dot) => {
      dot.classList.toggle('active', dot.dataset.section === sectionId);
    });
  }

  /** Atualiza o texto do breadcrumb (#breadcrumbCurrent) com o nome da secao. */
  private updateBreadcrumb(sectionId: SectionId): void {
    const el = qs('#breadcrumbCurrent');
    if (el) el.textContent = SECTION_BREADCRUMB_NAMES[sectionId];
  }
}

/**
 * Singleton do orquestrador de secoes — substitui o `var SectionManager` legado.
 * main.ts chama SectionManager.init(views, ctx) apos montar o DOM e o mapa.
 */
export const SectionManager = new ScrollSpySectionManager();
